import { markdownToPlainText } from '@littlebird/markdown-utils';
import {
  ChatStatus,
  isChatStatusBusy,
  useStores,
  type Message,
  type Thread,
} from '@littlebird/sdk';
import { useEffect, useState } from 'react';

import { settingsStore } from '@/stores/settings.store';

import {
  feedChatsStore,
  type FeedChatEntry,
  type FeedChatsStore,
} from './feed-chats.store';

export type FeedChatView = {
  threadId: string;
  title: string;
  status: 'working' | 'reply-ready' | 'stopped' | 'failed';
  snippet: string;
  ageAt: string;
};

const EMPTY_FEED_CHAT_ENTRIES: FeedChatEntry[] = [];

const toTimestamp = (value: Date | string | number | undefined): number => {
  const parsed =
    value instanceof Date
      ? value.getTime()
      : typeof value === 'number'
        ? value
        : value
          ? Date.parse(value)
          : 0;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const oneLine = (text: string | undefined, limit: number): string => {
  const normalized = text?.replace(/\s+/g, ' ').trim() ?? '';
  if (normalized.length <= limit) return normalized;
  return `${normalized.slice(0, limit - 1).trimEnd()}…`;
};

const titleFor = (thread: Thread, messages: Message[]): string => {
  const title = thread.title.trim();
  if (title && title !== 'New Chat') return oneLine(title, 100);
  const firstUserMessage = messages.find((message) => message.role === 'user');
  return (
    oneLine(
      firstUserMessage?.displayContent || firstUserMessage?.content,
      100
    ) || 'New chat'
  );
};

export const currentAssistantReply = (
  messages: Message[]
): Message | undefined => {
  let lastUserIndex = -1;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index]?.role === 'user') {
      lastUserIndex = index;
      break;
    }
  }
  return messages
    .slice(lastUserIndex + 1)
    .reverse()
    .find((message) => message.role === 'assistant');
};

const viewFor = (
  entry: FeedChatEntry,
  thread: Thread | undefined,
  messages: Message[],
  now: number
): FeedChatView => {
  const assistant = currentAssistantReply(messages);
  const threadStatus = thread?.status;
  const messageStatus = assistant?.status;
  const lastActivityAt = Math.max(
    entry.startedAt,
    ...messages.map((message) => toTimestamp(message.createdAt))
  );
  const view: FeedChatView = {
    threadId: entry.threadId,
    title: thread ? titleFor(thread, messages) : 'New chat',
    status: 'working',
    snippet: '',
    ageAt: new Date(lastActivityAt > 0 ? lastActivityAt : now).toISOString(),
  };

  if (!thread || isChatStatusBusy(threadStatus)) return view;

  if (
    threadStatus === ChatStatus.Errored ||
    messageStatus === ChatStatus.Errored
  ) {
    view.status = 'failed';
  } else if (
    threadStatus === ChatStatus.Stopped ||
    messageStatus === ChatStatus.Stopped
  ) {
    view.status = 'stopped';
  } else if (threadStatus === ChatStatus.Done) {
    view.status = 'reply-ready';
  } else if (!assistant) {
    return view;
  } else {
    view.status = 'reply-ready';
  }
  const completedAt =
    toTimestamp(thread.completedAt) ||
    toTimestamp(entry.completedAt) ||
    toTimestamp(assistant?.updatedAt) ||
    lastActivityAt;
  view.ageAt = new Date(completedAt).toISOString();
  view.snippet = assistant
    ? oneLine(
        markdownToPlainText(
          assistant.displayContent || assistant.content || ''
        ),
        180
      )
    : '';
  return view;
};

export const isFeedChatSettled = (
  entry: FeedChatEntry,
  thread: Thread | undefined,
  messages: Message[]
): boolean => viewFor(entry, thread, messages, Date.now()).status !== 'working';

const viewsFor = (
  entries: FeedChatEntry[],
  threads: ReturnType<typeof useStores>['threads'],
  messages: ReturnType<typeof useStores>['messages'],
  holdMinutes: number,
  now: number
) => {
  const holdMs = holdMinutes * 60_000;
  const working: FeedChatView[] = [];
  const ready: FeedChatView[] = [];

  for (const entry of entries) {
    if (entry.seen) continue;
    const thread = threads.getById(entry.threadId);
    const view = viewFor(
      entry,
      thread,
      thread ? messages.getMessagesByThreadId(entry.threadId) : [],
      now
    );
    const holdUntil = Date.parse(view.ageAt) + holdMs;
    if (view.status === 'working' || (holdMs > 0 && now < holdUntil))
      working.push(view);
    else ready.push(view);
  }

  return { working, ready };
};

export const useFeedChatViews = (
  store: FeedChatsStore = feedChatsStore,
  enabled = true
) => {
  'use no memo';

  const { threads, messages } = useStores();
  const holdMinutes = settingsStore.feedChatWorkingHoldMinutes;
  const entries = enabled ? store.entries : EMPTY_FEED_CHAT_ENTRIES;
  return viewsFor(entries, threads, messages, holdMinutes, Date.now());
};

export const useFeedChats = (
  store: FeedChatsStore = feedChatsStore,
  enabled = true
) => {
  'use no memo';

  const { threads, messages } = useStores();
  const entries = enabled ? store.entries : EMPTY_FEED_CHAT_ENTRIES;
  const holdMinutes = settingsStore.feedChatWorkingHoldMinutes;
  const [expiryTick, setExpiryTick] = useState(0);
  const views = useFeedChatViews(store, enabled);

  useEffect(() => {
    for (const view of [...views.working, ...views.ready]) {
      const thread = threads.getById(view.threadId);
      if (thread && isChatStatusBusy(thread.status)) {
        store.setCompletedAt(view.threadId, undefined);
      } else if (
        view.status !== 'working' &&
        (thread?.completedAt || messages.isThreadLoaded(view.threadId))
      ) {
        store.setCompletedAt(view.threadId, Date.parse(view.ageAt));
      }
    }
  }, [messages, store, threads, views.working, views.ready]);

  useEffect(() => {
    const refresh = () => setExpiryTick((tick) => tick + 1);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, []);

  useEffect(() => {
    const threadIds = entries.map((entry) => entry.threadId);
    if (threadIds.length > 0) {
      void messages.loadThreadsMessages(threadIds).catch(() => {});
    }

    for (const entry of entries) {
      if (threads.getById(entry.threadId)) continue;
      void threads
        .ensureLoaded(entry.threadId)
        .then((thread) => {
          if (!thread) store.dismiss(entry.threadId);
        })
        .catch(() => store.dismiss(entry.threadId));
    }
  }, [entries, messages, store, threads]);

  useEffect(() => {
    const holdMs = holdMinutes * 60_000;
    if (holdMs <= 0) return;

    const nextExpiry = views.working.reduce((earliest, view) => {
      if (view.status === 'working') return earliest;
      const expiresAt = Date.parse(view.ageAt) + holdMs;
      return Math.min(earliest, expiresAt);
    }, Number.POSITIVE_INFINITY);
    if (!Number.isFinite(nextExpiry)) return;

    const timeout = window.setTimeout(
      () => setExpiryTick((tick) => tick + 1),
      Math.max(0, nextExpiry - Date.now())
    );
    return () => window.clearTimeout(timeout);
  }, [entries, expiryTick, holdMinutes, views.working]);

  return views;
};
